import { captureAppError } from "@/utils/monitoring/sentry";
import {
  ContentContainer,
  Icon,
} from "@twinkltech/curriculum-architect-component-library";
import {
  ApiErrorResponse,
  Curriculum,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, Text } from "@twinkltech/web-ui";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import no_curr_content_image from "@/assets/images/no-curr-content.png";

import { GenericError } from "@/components/GenericError/GenericError";
import { NewCurriculumModalContainer } from "@/components/NewCurriculumModal/NewCurriculumModal.container";
import { Tag } from "@/components/Tag/Tag";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";

import { ActionMenu } from "./component/ActionMenu/ActionMenu";
import { FilterButton } from "./component/FilterButton/FilterButton";
import { CURRICULUM_LIST_NS } from "./CurriculumList.i18n";
import { useScrollPosition } from "./hooks/useScrollPosition";


type Filter = "all" | "active" | "archived";

export const CurriculumList = ({
  curriculumList = [],
  error,
  isLoading,
  isSchoolAccount = false,
}: {
  error?: ApiErrorResponse | null;
  curriculumList?: Curriculum[];
  isLoading: boolean;
  isSchoolAccount?: boolean;
}) => {
  const { t } = useTranslation(CURRICULUM_LIST_NS);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [newCurrOpen, setNewCurrOpen] = useState(false);

  const [filter, setFilter] = useState<Filter>("all");

  const { click } = useTwinklAnalytics();

  // Use our custom hook for horizontal and vertical scroll detection
  const { containerRef, isScrolledFromRightEdge: isActionMenuSticky } =
    useScrollPosition({
      initialState: false,
      ref: tableContainerRef,
    });

  // make the priority curr the first one, then sort the rest by most recently created at date
  const sortedCurriculumList = [...curriculumList]
    .sort((a, b) => {
      return b.createdAt.localeCompare(a.createdAt);
    })
    .filter((curriculum) =>
      filter === "all" ? true : curriculum.status === filter
    );

  const overflowBorderStyles = [
    "before:h-full before:opacity-0 before:pointer-events-none",
    "before:border-l",
    "before:absolute before:inset-0",
    "before:transition-opacity before:duration-100 before:ease-in-out",
    isActionMenuSticky ? "before:opacity-100" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Define thead border styles with transition
  const theadBorderStyles = [
    "sticky top-0 bg-white z-1",
    "after:absolute after:inset-0 after:pointer-events-none after:border-b after:border-gray-200 after:transition-opacity after:duration-100 after:ease-in-out",
  ]
    .filter(Boolean)
    .join(" ");

  if (isLoading) {
    return <LoadingPage />;
  }
  if (!isLoading && error) {
    captureAppError(error, {
      action: "load",
      area: "curriculum-list",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <div className="mb-24">
        <Text
          as="p"
          className="gradient-header font-bold max-w-[450px]"
          variant="5xl-bold"
        >
          <Trans
            i18nKey="welcome"
            ns={CURRICULUM_LIST_NS}
            values={{
              userName: "Admin profile",
              br: <br />,
            }}
          />
        </Text>
      </div>
      <div className="flex justify-between mb-10">
        <div className="flex flex-col gap-1">
          <Text as="h1" variant="3xl-medium">
            {t("title")}
          </Text>
          <Text className="max-w-[545px]" variant="lg-normal">
            {t("sub_title")}
          </Text>
        </div>
        <div>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => {
              setNewCurrOpen(true);
              click("click_cta", {
                ctaCopy: "Add New Curriculum",
              });
            }}
          >
            <Icon icon="Add" />
            {t("actions.add_new_curriculum")}
          </Button>
          <NewCurriculumModalContainer
            open={newCurrOpen}
            setOpen={setNewCurrOpen}
          />
        </div>
      </div>
      <div className="mb-6">
        <div
          aria-label={t("filters.curriculum_filter_label")}
          className="flex gap-4 mb-12"
          role="group"
        >
          <FilterButton
            aria-controls="curriculum-table"
            id="filter-option-all"
            isActive={filter === "all"}
            nextFilterId="filter-option-active"
            onClick={() => {
              setFilter("all");
              click("click_cta", {
                ctaCopy: "Filter: All",
              });
            }}
          >
            {t("filters.all_curriculum")}
          </FilterButton>
          <FilterButton
            aria-controls="curriculum-table"
            id="filter-option-active"
            isActive={filter === "active"}
            nextFilterId="filter-option-archived"
            previousFilterId="filter-option-all"
            onClick={() => {
              setFilter("active");
              click("click_cta", {
                ctaCopy: "Filter: Active Curricula",
              });
            }}
          >
            {t("filters.active_curriculum")}
          </FilterButton>
          <FilterButton
            aria-controls="curriculum-table"
            id="filter-option-archived"
            isActive={filter === "archived"}
            previousFilterId="filter-option-active"
            onClick={() => {
              setFilter("archived");
              click("click_cta", {
                ctaCopy: "Filter: Archived Curricula",
              });
            }}
          >
            {t("filters.archived_curriculum")}
          </FilterButton>
        </div>
      </div>
      <div
        ref={containerRef}
        className={[
          "w-full overflow-x-auto overflow-y-auto max-h-[calc(100vh-250px)] min-h-[300px]",
          sortedCurriculumList.length && "border-b",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ height: "auto" }}
      >
        {!isLoading && !sortedCurriculumList.length ? (
          <div className="flex flex-col items-center w-full border-gray-100 border rounded-lg">
            <div className="flex flex-col items-center justify-center gap-2 my-[120px] max-w-[410px] text-center">
              <img
                alt=""
                className="w-[175px]"
                role="presentation"
                src={no_curr_content_image}
              />
              <Text variant="2xl-medium">{t(`empty.${filter}.title`)}</Text>
              <Trans
                i18nKey={`empty.${filter}.content`}
                ns={CURRICULUM_LIST_NS}
                values={{ p: <Text variant="base-normal" /> }}
              />
            </div>
          </div>
        ) : null}

        {isLoading && (
          <div className="flex flex-col items-center justify-center my-[120px]">
            <Text variant="2xl-medium">
              {t("actions.loading", { ns: COMMON_NS })}
            </Text>
          </div>
        )}

        {!isLoading && sortedCurriculumList.length ? (
          <table
            className="min-w-[900px] border-collapse border-spacing-0  w-full"
            id="curriculum-table"
          >
            <caption className="sr-only">{t("list_table_caption")}</caption>
            <thead className={theadBorderStyles}>
              <tr className="h-[72px]">
                <th className="py-150 px-300 text-left" scope="col">
                  <Text variant="base-normal">
                    {t("list_table_headers.curriculum_name")}
                  </Text>
                </th>
                {/* We only display access information to school accounts */}
                {isSchoolAccount ? (
                  <th
                    className="py-150 px-300 text-left whitespace-nowrap min-w-min"
                    scope="col"
                  >
                    <Text variant="base-normal">
                      {t("list_table_headers.visibility")}
                    </Text>
                  </th>
                ) : null}
                <th
                  className="py-150 px-300 text-left whitespace-nowrap min-w-min"
                  scope="col"
                >
                  <Text variant="base-normal">
                    {t("list_table_headers.status")}
                  </Text>
                </th>
                <th
                  className="py-150 px-300 whitespace-nowrap text-left w-[140px]"
                  dir="col"
                  scope="col"
                >
                  <Text variant="base-normal">
                    {t("list_table_headers.progress")}
                  </Text>
                </th>
                <th
                  aria-label="Actions"
                  className="w-[60px] sticky right-0 bg-white z-1"
                  scope="col"
                >
                  <div
                    className={[
                      "min-h-[72px] h-800 absolute inset h-full w-full",
                      overflowBorderStyles,
                      theadBorderStyles,
                    ].join(" ")}
                  >
                    <span className="sr-only">
                      {t("list_table_headers.actions")}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCurriculumList?.map((curriculum) => (
                <tr
                  key={curriculum.id}
                  className="h-[80px] relative border-b border-b-neutral-150"
                >
                  <td className="min-w-[150px] max-w-0 w-full">
                    <Text
                      aria-label={t("curriculum_link_aria", {
                        curriculumName: curriculum.displayLabel,
                      })}
                      as={Link}
                      className="my-150 mx-300 block truncate hover:text-blue-500 focus:text-blue-500 active:text-blue-400"
                      to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
                        curriculum_id: curriculum.id,
                      })}
                    >
                      {curriculum.displayLabel}
                    </Text>
                  </td>

                  {isSchoolAccount ? (
                    <td className="py-150 px-300 whitespace-nowrap">
                      <Tag
                        variant={
                          curriculum.ownerType === "USER" ? "private" : "school"
                        }
                      />
                    </td>
                  ) : null}
                  <td className="py-150 px-300 whitespace-nowrap">
                    <Tag variant={curriculum.status} />
                  </td>
                  <td className="py-150 px-300">
                    <Tag variant={curriculum.progress} />
                  </td>
                  <td className="sticky right-0">
                    <div
                      className={[
                        "py-200 px-300 min-h-[80px] h-full bg-white",
                        overflowBorderStyles,
                      ].join(" ")}
                    >
                      <ActionMenu curriculum={curriculum} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </ContentContainer>
  );
};
